import React, { Component } from 'react'
import { alpha, styled } from '@mui/material/styles';
import classNames from 'classnames';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import AddOutlined from '@mui/icons-material/AddOutlined';
import RemoveOutlined from '@mui/icons-material/RemoveOutlined';
import ViewAgendaOutlined from '@mui/icons-material/ViewAgendaOutlined';
import ViewHeadlineOutlined from '@mui/icons-material/ViewHeadlineOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { withTranslation } from 'react-i18next';
import TranslationFields from './TranslationFields';
import TranslationFieldsPagination from './TranslationFieldsPagination.js';
import { UpgradePlanHint } from './PricingPlans';
import LoadingIndicator from '../components/LoadingIndicator';

const PREFIX = 'CollapsableFields';
const classes = {
  root: `${PREFIX}-root`,
  collapseContainer: `${PREFIX}-collapseContainer`,
  divider: `${PREFIX}-divider`,
  overlay: `${PREFIX}-overlay`,
  label: `${PREFIX}-label`,
  toggleIcon: `${PREFIX}-toggleIcon`,
  cta: `${PREFIX}-cta`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    marginLeft: '-24px',
    marginRight: '-24px',
    backgroundColor: theme.palette.surfaceRecessed,
    position: 'relative'
  },
  [`& .${classes.collapseContainer}`]: {
    // image fields render as full-width rows (Translate & Adapt style), stacked like text fields
    '&.id-images .static-list': {
      padding: '24px',
    }
  },
  [`& .${classes.divider}`]: {
    //margin: '0 -24px',
  },
  [`& .${classes.overlay}`]: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    background: alpha(theme.palette.background.paper, 0.5),
    display: 'flex',
    justifyContents: 'center',
    marginTop: 100,
  },
  [`& .${classes.label}`]: {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    padding: '12px 24px',
    fontSize: '1rem',
    // the label spans the header, so a click that misses the view toggle at the right end would
    // collapse the group: keep the toggle's corner out of the label's click area
    '&.with-view-toggle': {
      marginRight: 96,
    },
    '&.disabled': {
      opacity: .5,
      cursor: 'default'
    }
  },
  [`& .${classes.toggleIcon}`]: {
    margin: '-2px 12px 0 0'
  },
  [`& .${classes.cta}`]: {
    margin: '12px 0',
    padding: '24px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    border: `dashed 1px ${theme.palette.accentSurface.text}`,
    backgroundColor: theme.palette.accentSurface.background,
    '& p': {
      color: theme.palette.accentSurface.text,
    },
    '& span': {
      color: theme.palette.accentSurface.text,
    },
  },
}));

class CollapsableFields extends React.Component {


  constructor(props) {
    super(props);

    this.state = {
      showInfo: true,
      collapsed: true,
      updateNumber: 0,
      // images group only: big side-by-side previews instead of thumbnail rows. Off by
      // default, kept across pages, shared between every mounted images group via an event
      largeImages: localStorage.getItem('ly_largeImages') === 'true',
    };
  }

  componentDidMount() {
    if(this.props.id === 'images') window.addEventListener('ly-large-images', this.handleLargeImagesEvent);
  }

  componentWillUnmount() {
    window.removeEventListener('ly-large-images', this.handleLargeImagesEvent);
  }

  handleLargeImagesEvent = (event) => {
    this.setState({ largeImages: event.detail === true });
  }

  handleToggleLargeImages = (event) => {
    event.stopPropagation();
    const largeImages = !this.state.largeImages;
    localStorage.setItem('ly_largeImages', String(largeImages));
    window.dispatchEvent(new CustomEvent('ly-large-images', { detail: largeImages }));
  }


  componentWillReceiveProps = (newProps) => {
    //console.log(newProps.forceOpen, this.props.parentField)

    if(this.props.forceUpdate !== newProps.forceUpdate && newProps.forceOpen === this.props.parentField) {
      //alert(this.props.forceOpen)
      //if(this.props.forceOpen !== newProps.forceOpen) {
        this.handleToggleCollapse(null);
      //}
    }

    if(this.props.isSubfieldsPending === true && newProps.isSubfieldsPending === false) {
      //this.setState({updateNumber: Date.now()})

      this.forceUpdate();
    }
  }

  handleClick = (evt) => {
    this.setState({
      showInfo: false
    })
  }

  handleToggleCollapse = (evt) => {
    if(this.props.disabled) return null;

    if(this.state.collapsed === true) {
      if(this.props.onExpand) {
        this.props.onExpand(this.props.id)
      }
    }

    this.setState({
      collapsed: !this.state.collapsed
    })
  }

  handleChange = (event, data) => {
    this.props.onChange(null, {
      ...data,
      parentField: this.props.id
    });
  }

  getPaginatedFields = (fields, currentPage, totalCount) => {
    let paginatedFields = [];
    let startIndex = (currentPage-1)*100;
    let endIndex = startIndex+100;
    if(endIndex > totalCount) {
      endIndex = totalCount;
    }
    for(let i=startIndex; i<endIndex; i++) {
      //console.log(i, fields[i])
      paginatedFields.push(fields[i]);
    }
    return paginatedFields;
  }

  render() {
    const {
      fields,
      isFirst,
      parentField,
      onSubfieldsExpand,
      onSubfieldsExpanded,
      onSubfieldsCollapsed,
      id,
      itemId,
      paginated,
      currentPage,
      totalCount,
      subfieldsPaginateFunc
    } = this.props;

    const {
      showInfo,
      collapsed,
    } = this.state;

    return (
      <Root className={classNames('collapsable-fields', isFirst && 'first', this.props.type, this.props.id, classes.root)}>
        <Divider className={classes.divider} />

        <Typography className={classNames(classes.label, this.props.disabled ? 'disabled' : null, (this.props.id === 'images' && !collapsed && !this.props.locked) && 'with-view-toggle')} variant="h5" component="p" onClick={this.handleToggleCollapse}>
          {collapsed ? <AddOutlined fontSize={'small'} className={classes.toggleIcon} /> : <RemoveOutlined fontSize={'small'} className={classes.toggleIcon} />}
          {this.props.label}
        </Typography>
        {(this.props.id === 'images' && !collapsed && !this.props.locked) &&
          <Tooltip title={this.props.t(this.state.largeImages ? 'imageField.smallViewBtn' : 'imageField.largeViewBtn')} arrow>
            <IconButton
              size="medium"
              onClick={this.handleToggleLargeImages}
              // a generous hit area: a near miss lands on the button, or on nothing
              sx={{ position: 'absolute', top: 2, right: 16, padding: '12px' }}
              color={this.state.largeImages ? 'secondary' : 'default'}
            >
              {/* shows the view the click switches to: lines for the list, two panels for the large view */}
              {this.state.largeImages ? <ViewHeadlineOutlined fontSize="small" /> : <ViewAgendaOutlined fontSize="small" />}
            </IconButton>
          </Tooltip>
        }
        {this.props.locked &&
          <UpgradePlanHint neededPlan={'basic'} position="end" placement="bottom" top={8} isOverlay />
        }

        <Collapse
          in={!collapsed}
          mountOnEnter={true}
          unmountOnExit={true}

          onEnter={onSubfieldsExpand ? onSubfieldsExpand : null}
          onEntered={onSubfieldsExpanded ? onSubfieldsExpanded : null}
          onExited={onSubfieldsCollapsed ? onSubfieldsCollapsed : null}
        >
          <div className={classNames(classes.collapseContainer, `id-${this.props.id}`, `type-${this.props.type}`)}>
            <Divider className={classes.divider} />


            {paginated && this.props.totalCount > 100 &&
              <TranslationFieldsPagination
                scope={id}
                itemId={itemId}
                perPage={100}
                totalCount={totalCount}
                currentPage={currentPage}
                subfieldsPaginateFunc={this.props.subfieldsPaginateFunc}
                position="below"
              />
            }

            <TranslationFields
              //key={`translationfields-${this.props.type}-${this.props.id}-${this.state.updateNumber}`}
              data={paginated ? this.getPaginatedFields(fields, currentPage, totalCount) : fields}
              nested
              itemId={this.props.itemId}
              id={this.props.id}
              parentField={parentField}
              largeImages={this.state.largeImages}
              isPending={this.props.isPending}
              isSubfieldsPending={this.props.isSubfieldsPending}
              onChange={this.handleChange}
              from={this.props.from}
              to={this.props.to}
              forceUpdate={this.props.forceUpdate}
              triggerClearAllTranslations={this.props.triggerClearAllTranslations}
              triggerDiscardChanges={this.props.triggerDiscardChanges}
              emptyMessage={this.props.emptyMessage}


              paginated={paginated}
              totalCount={totalCount}
              currentPage={currentPage}
              subfieldsPaginateFunc={subfieldsPaginateFunc}
            />

            {paginated && totalCount > 100 &&
              <TranslationFieldsPagination
                scope={id}
                itemId={itemId}
                perPage={100}
                totalCount={totalCount}
                currentPage={currentPage}
                subfieldsPaginateFunc={subfieldsPaginateFunc}
                position="above"
              />
            }

            {this.props.isSubfieldsPending &&
              <div className={classes.overlay}>
                {/*<CircularProgress size={20} />*/}
              </div>
            }

            {/*<LoadingIndicator
              pending={this.props.isSubfieldsPending}
            />*/}
          </div>
        </Collapse>

      </Root>
    )
  }
}

export default withTranslation()(CollapsableFields);
